import SEO from "@/components/SEO";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { ArrowButton } from "@/components/ArrowButton";
import SectionHeading from "@/components/SectionHeading";
import NeuroIntro from "@/sections/NeuroIntro";
import NeuroMap from "@/sections/NeuroMap";
import Timeline from "@/sections/Timeline";
import CtaBand from "@/sections/CtaBand";
import { waTopic, slugify } from "@/data/site";

const QUANDO = [
  "Atenção",
  "Memória",
  "Aprendizagem",
  "Organização",
  "Funções executivas",
  "Desenvolvimento",
  "Funcionamento cognitivo",
  "Dificuldades escolares",
  "Alterações percebidas no funcionamento",
  "Investigação clínica de hipóteses como TDAH ou TEA",
  "Necessidade de reavaliação",
];

const Neuropsicologia = () => (
  <>
    <SEO
      title="Neuropsicologia e Avaliação Neuropsicológica | Sabrina Mota Neuropsicóloga"
      description="O que é Neuropsicologia e como funciona a avaliação neuropsicológica para crianças, adolescentes e adultos — Sabrina Mota, Psicóloga e Neuropsicóloga CRP 06/201923."
      path="/neuropsicologia"
    />
    <div data-testid="neuropsicologia-page">
      <NeuroIntro />
      <NeuroMap />

      <section className="py-20 lg:py-28" data-testid="avaliacao-explain-section">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Avaliação Neuropsicológica" title="E o que é uma Avaliação Neuropsicológica?" testId="avaliacao-explain" />
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.1} className="space-y-5 text-base leading-relaxed text-ink/70">
              <p>
                A avaliação neuropsicológica é um processo clínico estruturado que integra diferentes fontes de informação para compreender aspectos do funcionamento cognitivo, emocional e comportamental de uma pessoa.
              </p>
              <p>
                O processo pode envolver entrevista clínica, observação, análise da história de desenvolvimento e utilização de instrumentos psicológicos adequados à idade, à demanda e aos objetivos da avaliação.
              </p>
              <p className="text-sm text-ink/50">
                Não se trata de um único teste — é um processo com etapas, conduzido conforme as normas do Conselho Federal de Psicologia.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <div className="bg-cream">
        <Timeline />
      </div>

      <section className="py-20 lg:py-28" data-testid="quando-avaliacao-section">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow="Demandas"
              title="Quando uma avaliação pode ser considerada?"
              lead="Diferentes demandas podem motivar uma avaliação. A pertinência do processo é sempre analisada individualmente, a partir da entrevista inicial."
              testId="quando-avaliacao"
            />
          </div>
          <div className="lg:col-span-6">
            <Stagger className="flex flex-wrap content-start gap-3 lg:pt-14" gap={0.06}>
              {QUANDO.map((d) => (
                <StaggerItem key={d}>
                  <span
                    data-testid={`quando-chip-${slugify(d)}`}
                    className="inline-block rounded-full border border-raiz-800/20 bg-cream px-5 py-2.5 text-sm text-raiz-900 transition-colors duration-300 hover:bg-raiz-800 hover:text-paper"
                  >
                    {d}
                  </span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <section className="border-t border-raiz-900/10 bg-sand/50 py-20 lg:py-28" data-testid="pais-section">
        <div className="container-x grid items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <p className="eyebrow">Para pais e responsáveis</p>
            </FadeUp>
            <FadeUp delay={0.08}>
              <h2 className="mt-5 font-serif text-3xl font-light leading-[1.12] tracking-tight text-ink sm:text-4xl" data-testid="pais-title">
                Meu filho precisa de avaliação?
              </h2>
            </FadeUp>
            <FadeUp delay={0.16} className="mt-6 max-w-xl space-y-5 text-base leading-relaxed text-ink/70">
              <p>
                Dificuldades escolares, de atenção, comportamento ou desenvolvimento podem ter diferentes explicações. Por isso, uma avaliação não deve começar pela busca de um rótulo, mas pela compreensão cuidadosa da demanda e da história da criança.
              </p>
              <p>
                Quando indicada, a avaliação neuropsicológica pode contribuir para uma compreensão mais ampla de diferentes aspectos do funcionamento da criança.
              </p>
            </FadeUp>
            <FadeUp delay={0.24} className="mt-8">
              <ArrowButton href={waTopic("quando uma avaliação pode ser indicada para uma criança")} target="_blank" rel="noopener noreferrer" testId="pais-cta">
                Entender quando uma avaliação pode ser indicada
              </ArrowButton>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="lg:col-span-5">
            <div className="rounded-3xl border border-raiz-900/10 bg-paper p-8">
              <p className="font-serif text-xl italic leading-relaxed text-raiz-800">
                "Compreender antes de concluir: a história e o contexto da criança sempre vêm primeiro."
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="adultos-section">
        <div className="container-x grid items-center gap-10 lg:grid-cols-12">
          <FadeUp delay={0.1} className="lg:col-span-5">
            <div className="rounded-3xl border border-raiz-900/10 bg-cream p-8">
              <p className="font-serif text-xl italic leading-relaxed text-raiz-800">
                "Adultos também podem compreender melhor a própria trajetória por meio de uma avaliação."
              </p>
            </div>
          </FadeUp>
          <div className="lg:col-span-7">
            <FadeUp>
              <p className="eyebrow">Vida adulta</p>
            </FadeUp>
            <FadeUp delay={0.08}>
              <h2 className="mt-5 font-serif text-3xl font-light leading-[1.12] tracking-tight text-ink sm:text-4xl" data-testid="adultos-title">
                Avaliação Neuropsicológica também é para adultos?
              </h2>
            </FadeUp>
            <FadeUp delay={0.16} className="mt-6 max-w-xl space-y-5 text-base leading-relaxed text-ink/70">
              <p>
                Sim. Adultos também podem procurar avaliação neuropsicológica diante de questões relacionadas à atenção, memória, organização, aprendizagem, funcionamento cognitivo ou quando existe indicação para investigação clínica.
              </p>
              <p>
                A necessidade e os objetivos da avaliação são definidos individualmente a partir da demanda apresentada.
              </p>
            </FadeUp>
            <FadeUp delay={0.24} className="mt-8">
              <ArrowButton href={waTopic("a avaliação neuropsicológica para adultos")} target="_blank" rel="noopener noreferrer" testId="adultos-cta">
                Quero saber mais
              </ArrowButton>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="border-t border-raiz-900/10 py-20 lg:py-28" data-testid="reavaliacao-section">
        <div className="container-x max-w-4xl">
          <FadeUp>
            <p className="eyebrow">Reavaliação</p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h2 className="mt-5 font-serif text-3xl font-light leading-[1.12] tracking-tight text-ink sm:text-4xl lg:text-5xl" data-testid="reavaliacao-title">
              Já possui uma avaliação anterior?
            </h2>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="lead mt-7 max-w-2xl">
              Em determinadas situações, a reavaliação neuropsicológica pode ser indicada para compreender o funcionamento atual e analisar possíveis mudanças ao longo do desenvolvimento, tratamento ou trajetória acadêmica e funcional.
            </p>
          </FadeUp>
          <FadeUp delay={0.24} className="mt-9">
            <ArrowButton href={waTopic("a reavaliação neuropsicológica")} target="_blank" rel="noopener noreferrer" testId="reavaliacao-cta-button">
              Conversar sobre reavaliação
            </ArrowButton>
          </FadeUp>
        </div>
      </section>

      <CtaBand title="Ficou com alguma dúvida?" text="O primeiro contato pode ajudar a compreender se a avaliação neuropsicológica é o caminho mais adequado para você ou sua família." />
    </div>
  </>
);

export default Neuropsicologia;
